/**
 * OWL2 Ontology Query Utilities
 *
 * Helper functions for querying parsed Ontology structures.
 * These functions provide convenient access to common query patterns.
 */

import type {
    Ontology,
    Declaration,
    SubClassOf,
    AnnotationAssertion,
    ObjectPropertyDomain,
    ObjectPropertyRange,
    DataPropertyDomain,
    DataPropertyRange,
    SubObjectPropertyOf,
    ClassExpression,
    IRI
} from './index.ts';

import {
    isDeclaration,
    isSubClassOf,
    isAnnotationAssertion,
    isObjectPropertyDomain,
    isObjectPropertyRange,
    isDataPropertyDomain,
    isDataPropertyRange,
    isSubObjectPropertyOf,
    isClass,
    isClassEntity,
    isObjectPropertyEntity,
    isDataPropertyEntity,
    iriToString,
    isObjectProperty,
    isFullIRI,
    isAbbreviatedIRI,
    RDFS_NAMESPACE
} from './index.ts';

/**
 * Check if a value is an IRI (FullIRI or AbbreviatedIRI)
 */
function isIRI(value: unknown): value is IRI {
    return isFullIRI(value as any) || isAbbreviatedIRI(value as any);
}

// =============================================================================
// Type Aliases
// =============================================================================

/**
 * Map from class URI to array of parent class URIs
 */
export type ClassHierarchyMap = Map<string, string[]>;

// =============================================================================
// Axiom Query Functions
// =============================================================================

/**
 * Get all Declaration axioms from an ontology.
 */
export function getDeclarations(onto: Ontology): Declaration[] {
    return onto.axioms.filter(isDeclaration);
}

/**
 * Get all SubClassOf axioms from an ontology.
 */
export function getSubClassOfAxioms(onto: Ontology): SubClassOf[] {
    return onto.axioms.filter(isSubClassOf);
}

/**
 * Get all AnnotationAssertion axioms from an ontology.
 */
export function getAnnotationAssertions(onto: Ontology): AnnotationAssertion[] {
    return onto.axioms.filter(isAnnotationAssertion);
}

/**
 * Get all ObjectPropertyDomain axioms from an ontology.
 */
export function getObjectPropertyDomains(onto: Ontology): ObjectPropertyDomain[] {
    return onto.axioms.filter(isObjectPropertyDomain);
}

/**
 * Get all ObjectPropertyRange axioms from an ontology.
 */
export function getObjectPropertyRanges(onto: Ontology): ObjectPropertyRange[] {
    return onto.axioms.filter(isObjectPropertyRange);
}

/**
 * Get all DataPropertyDomain axioms from an ontology.
 */
export function getDataPropertyDomains(onto: Ontology): DataPropertyDomain[] {
    return onto.axioms.filter(isDataPropertyDomain);
}

/**
 * Get all DataPropertyRange axioms from an ontology.
 */
export function getDataPropertyRanges(onto: Ontology): DataPropertyRange[] {
    return onto.axioms.filter(isDataPropertyRange);
}

/**
 * Get all SubObjectPropertyOf axioms from an ontology.
 */
export function getSubObjectPropertyOfAxioms(onto: Ontology): SubObjectPropertyOf[] {
    return onto.axioms.filter(isSubObjectPropertyOf);
}

// =============================================================================
// Entity Query Functions
// =============================================================================

/**
 * Get all class declarations from an ontology.
 */
export function getClassDeclarations(onto: Ontology): Declaration[] {
    return onto.axioms.filter(
        (a): a is Declaration => isDeclaration(a) && isClassEntity(a.entity)
    );
}

/**
 * Get all object property declarations from an ontology.
 */
export function getObjectPropertyDeclarations(onto: Ontology): Declaration[] {
    return onto.axioms.filter(
        (a): a is Declaration => isDeclaration(a) && isObjectPropertyEntity(a.entity)
    );
}

/**
 * Get all data property declarations from an ontology.
 */
export function getDataPropertyDeclarations(onto: Ontology): Declaration[] {
    return onto.axioms.filter(
        (a): a is Declaration => isDeclaration(a) && isDataPropertyEntity(a.entity)
    );
}

// =============================================================================
// Annotation Query Functions
// =============================================================================

/**
 * Get all annotations for a specific subject IRI.
 */
export function getAnnotationsForSubject(
    onto: Ontology,
    subjectIri: string
): AnnotationAssertion[] {
    return onto.axioms.filter(
        (a): a is AnnotationAssertion => {
            if (!isAnnotationAssertion(a)) return false;
            // AnnotationSubject can be IRI or AnonymousIndividual
            if (isIRI(a.subject)) {
                return iriToString(a.subject) === subjectIri;
            }
            return false;
        }
    );
}

/**
 * Get a specific annotation value for a subject.
 * Returns the first matching annotation's literal value as string, or undefined.
 */
export function getAnnotationValue(
    onto: Ontology,
    subjectIri: string,
    propertyIri: string
): string | undefined {
    const annotations = getAnnotationsForSubject(onto, subjectIri);
    for (const annotation of annotations) {
        if (iriToString(annotation.property.iri) === propertyIri) {
            const value = annotation.value;
            if ('lexicalForm' in value) {
                return value.lexicalForm;
            }
            // Check if value is an IRI (FullIRI or AbbreviatedIRI)
            if (isIRI(value)) {
                return iriToString(value);
            }
        }
    }
    return undefined;
}

/**
 * Get rdfs:label for a subject.
 */
export function getLabel(onto: Ontology, subjectIri: string): string | undefined {
    return getAnnotationValue(onto, subjectIri, RDFS_NAMESPACE + 'label');
}

/**
 * Get rdfs:comment for a subject.
 */
export function getComment(onto: Ontology, subjectIri: string): string | undefined {
    return getAnnotationValue(onto, subjectIri, RDFS_NAMESPACE + 'comment');
}

/**
 * Get a specific annotation value for a subject with language preference.
 * Returns the annotation value in the preferred language if available,
 * otherwise falls back to any available language.
 *
 * @param onto - The ontology to search
 * @param subjectIri - The IRI of the subject to get annotations for
 * @param propertyIri - The IRI of the annotation property (e.g., rdfs:label)
 * @param preferredLanguage - The preferred language tag (default: 'en')
 * @returns The annotation value as string, or undefined if not found
 */
export function getAnnotationValueWithLanguagePreference(
    onto: Ontology,
    subjectIri: string,
    propertyIri: string,
    preferredLanguage: string = 'en'
): string | undefined {
    const preferredValue: string | undefined = undefined;
    let fallbackValue: string | undefined = undefined;

    const annotations = getAnnotationsForSubject(onto, subjectIri);

    for (const annotation of annotations) {
        if (iriToString(annotation.property.iri) === propertyIri) {
            const value = annotation.value;

            if ('lexicalForm' in value) {
                // Check if this literal has a language tag
                if ('languageTag' in value && value.languageTag) {
                    if (value.languageTag === preferredLanguage) {
                        // Found preferred language - return immediately
                        return value.lexicalForm;
                    }
                    // Store as fallback if we don't have one yet
                    if (!fallbackValue) {
                        fallbackValue = value.lexicalForm;
                    }
                } else {
                    // No language tag - treat as generic fallback
                    if (!fallbackValue) {
                        fallbackValue = value.lexicalForm;
                    }
                }
            }
            // Check if value is an IRI
            else if (isIRI(value)) {
                if (!fallbackValue) {
                    fallbackValue = iriToString(value);
                }
            }
        }
    }

    return preferredValue || fallbackValue;
}

/**
 * Get rdfs:label for a subject with language preference.
 *
 * @param onto - The ontology to search
 * @param subjectIri - The IRI of the subject
 * @param preferredLanguage - The preferred language tag (default: 'en')
 */
export function getLabelWithLanguagePreference(
    onto: Ontology,
    subjectIri: string,
    preferredLanguage: string = 'en'
): string | undefined {
    return getAnnotationValueWithLanguagePreference(
        onto,
        subjectIri,
        RDFS_NAMESPACE + 'label',
        preferredLanguage
    );
}

// =============================================================================
// Class Hierarchy Functions
// =============================================================================

/**
 * Build a class hierarchy map from SubClassOf axioms.
 * Returns a map from class URI to array of direct parent class URIs.
 * Only includes SubClassOf axioms where both subClass and superClass are named classes.
 */
export function buildClassHierarchy(onto: Ontology): ClassHierarchyMap {
    const hierarchy = new Map<string, string[]>();

    for (const axiom of onto.axioms) {
        if (isSubClassOf(axiom) && isClass(axiom.subClass) && isClass(axiom.superClass)) {
            const childUri = iriToString(axiom.subClass.iri);
            const parentUri = iriToString(axiom.superClass.iri);

            const parents = hierarchy.get(childUri) || [];
            if (!parents.includes(parentUri)) {
                parents.push(parentUri);
            }
            hierarchy.set(childUri, parents);
        }
    }

    return hierarchy;
}

/**
 * Check if a class is a subclass of another (direct or transitive).
 * Also returns true if classUri equals targetUri (reflexive).
 */
export function isSubclassOf(
    classUri: string,
    targetUri: string,
    hierarchy: ClassHierarchyMap
): boolean {
    if (classUri === targetUri) return true;

    const visited = new Set<string>();
    const checkAncestors = (uri: string): boolean => {
        if (visited.has(uri)) return false;
        visited.add(uri);

        const parents = hierarchy.get(uri);
        if (!parents) return false;

        for (const parentUri of parents) {
            if (parentUri === targetUri) return true;
            if (checkAncestors(parentUri)) return true;
        }
        return false;
    };

    return checkAncestors(classUri);
}

/**
 * Get all SubClassOf axioms where the subClass is a specific named class.
 */
export function getSubClassOfAxiomsForClass(
    onto: Ontology,
    classIri: string
): SubClassOf[] {
    return onto.axioms.filter(
        (a): a is SubClassOf =>
            isSubClassOf(a) &&
            isClass(a.subClass) &&
            iriToString(a.subClass.iri) === classIri
    );
}

// =============================================================================
// Property Query Functions
// =============================================================================

/**
 * Get the IRI string from an ObjectPropertyExpression.
 * Returns undefined if it's an inverse property.
 */
export function getObjectPropertyIri(prop: { type: string; iri?: IRI }): string | undefined {
    if (isObjectProperty(prop as any)) {
        return iriToString((prop as any).iri);
    }
    return undefined;
}

/**
 * Get domain and range for an object property.
 */
export function getPropertyDomainRange(
    onto: Ontology,
    propertyIri: string
): { domain?: ClassExpression; range?: ClassExpression } {
    let domain: ClassExpression | undefined;
    let range: ClassExpression | undefined;

    for (const axiom of onto.axioms) {
        if (isObjectPropertyDomain(axiom)) {
            const propIri = getObjectPropertyIri(axiom.property);
            if (propIri === propertyIri) {
                domain = axiom.domain;
            }
        }
        if (isObjectPropertyRange(axiom)) {
            const propIri = getObjectPropertyIri(axiom.property);
            if (propIri === propertyIri) {
                range = axiom.range;
            }
        }
    }

    return { domain, range };
}

// =============================================================================
// Namespace Filtering Functions
// =============================================================================

/**
 * Check if an IRI belongs to any of the allowed namespaces.
 */
export function isInNamespace(iri: string, namespaces: string[]): boolean {
    return namespaces.some(ns => iri.startsWith(ns));
}

/**
 * Get all class URIs declared in the ontology that belong to allowed namespaces.
 */
export function getClassUrisInNamespaces(
    onto: Ontology,
    namespaces: string[]
): string[] {
    const classDeclarations = getClassDeclarations(onto);
    return classDeclarations
        .map(d => iriToString(d.entity.iri))
        .filter(uri => isInNamespace(uri, namespaces));
}

/**
 * Get all object property URIs declared in the ontology that belong to allowed namespaces.
 */
export function getObjectPropertyUrisInNamespaces(
    onto: Ontology,
    namespaces: string[]
): string[] {
    const propDeclarations = getObjectPropertyDeclarations(onto);
    return propDeclarations
        .map(d => iriToString(d.entity.iri))
        .filter(uri => isInNamespace(uri, namespaces));
}

/**
 * Get all data property URIs declared in the ontology that belong to allowed namespaces.
 */
export function getDataPropertyUrisInNamespaces(
    onto: Ontology,
    namespaces: string[]
): string[] {
    const propDeclarations = getDataPropertyDeclarations(onto);
    return propDeclarations
        .map(d => iriToString(d.entity.iri))
        .filter(uri => isInNamespace(uri, namespaces));
}
